'use client'
import { ButtonRoundStyle, Image as ImageCustom } from '@/components'
import { menuMobile } from '@/components/pages/accounts/aside/menu.config'
import { HEADER_MENU, INTERNAL_DATA } from '@/config/global-config'
import { CALLBACK_URL_KEY } from '@/constants'
import { IMG_PLACEHOLDER } from '@/constants/ui'
import { useLogout } from '@/features/auth'
import { PopupFormOrderLookup } from '@/features/orders/components/order-lookup/popup-form'
import Link from '@/features/ref/components/Link'
import { usePathname } from '@/libs/i18n/routing'
import { useAccountStore } from '@/store'
import { getTheImageUrl, getVND } from '@/utils/helpers'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import { Avatar, Box, Button, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import SubMenuMobile from './submenu-mobile'

type Props = {
  menuItems?: Array<{
    key: string
    link: string
    name?: string
    condition?: (isAuthenticated: boolean) => boolean
  }>
  disableTranslations?: boolean
  categories?: ICategoryMenu | null | undefined
}

export default function NavMenuMobile({
  menuItems = HEADER_MENU,
  disableTranslations = false,
  categories,
}: Props) {
  const [open, setOpen] = useState(false)
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null)
  const { isAuthenticated, data } = useAccountStore()

  const t = useTranslations()
  const pathname = usePathname()
  const { logout: handleLogout, isLoading } = useLogout()
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen)
  }
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  return (
    <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
      <IconButton aria-label="Menu" onClick={toggleDrawer(true)} sx={{ mr: 1, p: 0 }}>
        <MenuIcon sx={{ color: '#252422' }} />
      </IconButton>
      <Drawer open={open} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            backgroundColor: 'white',
            color: '#252422',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            height: '100%',
            p: 0,
            width: '320px',
            maxWidth: '100%',
          }}
        >
          {/* Header/logo section */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              p: '0.75rem 0.5rem',
              flex: '0 0 auto',
            }}
          >
            <Link href="/">
              <Image
                src={INTERNAL_DATA.logo}
                width={145}
                height={52}
                style={{ height: 52, width: 'auto' }}
                alt="Logo"
              />
            </Link>
            <IconButton
              onClick={toggleDrawer(false)}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                borderRadius: '4px',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              <CloseIcon sx={{ color: 'white' }} fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ width: '100%' }} />
          {/* Main body area will scroll */}
          <Box
            sx={{
              flex: '1 1 0%',
              minHeight: 0,
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflowY: 'auto',
              width: '100%',
            }}
          >
            <Box component={'ul'}>
              {menuMobile
                .filter((menuItem) => {
                  // Nếu menuItem có isAuthenticated = true thì chỉ show khi isAuthenticated true
                  if (menuItem.isAuthenticated && !isAuthenticated) return false
                  return true
                })
                .map((menuItem, index) => (
                  <Box component={'li'} key={index} sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: '18px',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      {menuItem.label}
                    </Typography>
                    <Box component={'ul'}>
                      {menuItem?.menus?.map((subItem, subIndex) => {
                        // Remove trailing slash helper
                        const normalizePath = (path: string) =>
                          path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
                        const currentPath = normalizePath(pathname)
                        const itemPath = normalizePath(subItem.path)

                        // Highlight rule, optimize logic
                        const isActive =
                          itemPath === '/accounts'
                            ? currentPath === '/accounts'
                            : currentPath.startsWith(itemPath) && itemPath !== '/'

                        // Check if menu item is coming soon
                        const isComingSoon = subItem.commingSoon === true

                        const hasDropdown = subItem.label === 'Cửa hàng'
                        const isSubMenuOpen = openSubMenu === subItem.label

                        const handleClick = (e: React.MouseEvent) => {
                          if (hasDropdown) {
                            e.preventDefault()
                            setOpenSubMenu(isSubMenuOpen ? null : subItem.label)
                          }
                        }

                        return (
                          <Box component="li" key={subIndex} sx={{ mb: 1 }}>
                            <Link
                              href={subItem.path}
                              style={isComingSoon || hasDropdown ? { pointerEvents: 'none' } : {}}
                            >
                              <Box
                                sx={{
                                  p: '.6em 0.4em',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 1,
                                  borderRadius: '4px',
                                  fontSize: '16px',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: isActive && !isComingSoon ? 'primary.main' : 'transparent',
                                  color: isComingSoon ? 'var(--sub-color)' : isActive ? '#fff' : 'inherit',
                                  cursor: isComingSoon ? 'not-allowed' : hasDropdown ? 'pointer' : 'pointer',
                                  opacity: isComingSoon ? 0.6 : 1,
                                  pointerEvents: hasDropdown || !isComingSoon ? 'auto' : undefined,
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  {subItem.icon} {subItem.label}
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  {/* Hiển thị tag Coming Soon */}
                                  {isComingSoon && (
                                    <Chip
                                      label="Sắp ra mắt"
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        borderColor: 'warning.main',
                                        color: 'warning.main',
                                      }}
                                    />
                                  )}

                                  {hasDropdown && (
                                    <KeyboardArrowDownIcon
                                      sx={{
                                        transform: isSubMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                      }}
                                      onClick={handleClick}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Link>
                            {hasDropdown && isSubMenuOpen && (
                              <SubMenuMobile categories={categories} open={isSubMenuOpen} />
                            )}
                          </Box>
                        )
                      })}
                    </Box>

                    {/* {menuItem?.path && (
                          <Link href={menuItem?.path}>
                            <ButtonRoundStyle
                              variant="outlined"
                              sx={{
                                borderRadius: '8px',
                                justifyContent: 'center',
                                gap: 1,
                              }}
                            >
                              Dashboard Affiliate
                              <WestIcon
                                sx={{
                                  width: '.8em',
                                  rotate: '180deg',
                                }}
                              />
                            </ButtonRoundStyle>
                          </Link>
                        )} */}
                  </Box>
                ))}
              <PopupFormOrderLookup isMobile={true} isDesktop={false} style={{ width: '100%' }} />
            </Box>
          </Box>
          {/* Sticky Footer section */}
          <Box
            sx={{
              p: 2,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flex: '0 0 auto',
              bgcolor: 'white',
            }}
          >
            {isAuthenticated && (
              <>
                <Divider sx={{ width: '100%', flex: '0 0 auto' }} />
                <Box display="flex" alignItems="start" flexDirection={'column'} gap={2}>
                  <Box display="flex" alignItems="center" gap={0.1}>
                    <Box minWidth={56} maxWidth={56} flexShrink={0}>
                      <Avatar
                        src={getTheImageUrl(data?.profile_picture) || '/assets/images/ava-default.png'}
                        sx={{ width: 48, height: 48 }}
                      ></Avatar>
                    </Box>
                    <Box display="flex" flexDirection="column">
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography fontWeight={600}>{data?.lastname} </Typography>
                        <ImageCustom
                          sx={{ width: { xs: '5rem', lg: '6rem' }, display: 'inline-block' }}
                          src={getTheImageUrl(data?.affiliate_information?.image ?? IMG_PLACEHOLDER)}
                          alt=""
                          slotProps={{ img: { style: { display: 'block' } } }}
                        />
                      </Stack>
                      {/* <Tooltip placement="bottom" title={t('Account.feature-in-progress')}> */}
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Image
                          src="/assets/images/ic-price.png"
                          alt=""
                          width={16}
                          height={16}
                          className="w-[1em] flex-shrink-0"
                        />
                        <span>{getVND(data?.affiliate_information?.total_wallet_money, ' ')} xu</span>
                      </Box>
                    </Box>
                  </Box>
                  <ButtonRoundStyle
                    variant="outlined"
                    sx={{
                      width: '100%',
                      borderRadius: '8px',
                      justifyContent: 'center',
                      borderColor: '#FDB0AAFF',
                      color: '#F04438',
                      '&:hover': {
                        borderColor: '#F04438',
                        backgroundColor: '#FFF0EF',
                      },
                      gap: 1,
                    }}
                    onClick={handleLogout}
                    loading={isLoading}
                  >
                    Đăng xuất
                    <LogoutIcon sx={{ color: '#F04438' }} fontSize="small" />
                  </ButtonRoundStyle>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  )
}
